import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { useAlphaFusion } from './useAlphaFusion';
import { useScreenType } from '../../hooks/useScreenType';

import DesktopAlphaFusion from './DesktopAlphaFusion';
import MobileAlphaFusion from './MobileAlphaFusion';

const AlphaFusionScreen: React.FC = () => {
    const { data, loading, error, refetch } = useAlphaFusion();
    const navigate = useNavigate();
    const { isMobile } = useScreenType();
    const [activeTab, setActiveTab] = useState<'opportunities' | 'sell' | 'universe' | 'info'>('opportunities');
    const [filters, setFilters] = useState({
        search: '',
        nviOnly: false,
        guppyOnly: false,
        bullFlagOnly: false,
        trixPositive: false,
        highScore: false,
        undervaluedOnly: false
    });

    if (loading) {
        return (
            <SiteLayout>
                <Header title="Kanea Alpha Fusion" />
                <Box>
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <div className="loading-spinner" />
                        <p>Analyse Smart Money (NVI) & Tendance (Guppy)...</p>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    if (error) {
        return (
            <SiteLayout>
                <Header title="Kanea Alpha Fusion" />
                <Box>
                    <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>
                        <p>Erreur: {error}</p>
                        <button onClick={refetch} style={{ marginTop: 16, padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Réessayer</button>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    const summary = data?.portfolio_summary;
    const opportunities = (data?.opportunities || []).filter((opp: any) => opp.is_bull_flag);
    const sellSignals = data?.sell_signals || [];
    const eligibleUniverse = data?.eligible_universe || [];
    const sentiment = data?.market_sentiment || 'NEUTRAL';

    const sentimentColors: Record<string, { bg: string; color: string }> = {
        BULLISH: { bg: 'rgba(22, 163, 74, 0.15)', color: '#4ade80' },
        BEARISH: { bg: 'rgba(220, 38, 38, 0.15)', color: '#f87171' },
        NEUTRAL: { bg: 'var(--bg-input)', color: 'var(--text-muted)' },
    };

    const filteredUniverse = eligibleUniverse.filter((stock: any) => {
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch =
                stock.symbole.toLowerCase().includes(searchLower) ||
                (stock.nom_societe && stock.nom_societe.toLowerCase().includes(searchLower));
            if (!matchesSearch) return false;
        }
        if (filters.nviOnly && !stock.nvi_bullish) return false;
        if (filters.guppyOnly && !stock.guppy_bullish) return false;
        if (filters.bullFlagOnly && !stock.is_bull_flag) return false;
        if (filters.trixPositive && (stock.trix || 0) <= 0) return false;
        if (filters.highScore && stock.score < 150) return false;
        if (filters.undervaluedOnly && !stock.is_undervalued) return false;
        return true;
    });

    const childProps = {
        data,
        activeTab,
        setActiveTab,
        filters,
        setFilters,
        opportunities,
        sellSignals,
        filteredUniverse,
        sentiment,
        sentimentColors,
        navigate: (path: string) => navigate(path),
    };

    return (
        <SiteLayout>
            <Header title="Kanea Alpha Fusion 🚀" />
            {isMobile ? (
                <MobileAlphaFusion {...childProps} />
            ) : (
                <DesktopAlphaFusion {...childProps} />
            )}
        </SiteLayout>
    );
};

export default AlphaFusionScreen;
